import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';
import Yup from '../../utils/yup';
import utc from 'dayjs/plugin/utc';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { IconButton } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import masterDataService from '../../service/masterData.service';
import useUsers from '../../context/UsersContext';
import { cleanValues, generateAutocompleteItems, getJokerFieldType } from '../../utils/helper';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI, FormBase, DialogUI } from '../Interface';
import sourcelistService from '../../service/sourcelist.service';
import SourceListFields from './SourceListFields';
import History from '../History/History';
import SourceListReports from './SourceListReports';
import SourceListHistoryDialog from './SourceListHistoryDialog';
import SourceListTabs from './SourceListTabs';
import SourceListTree from './SourceListTree';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';
import { useLoader } from '../../provider/LoaderProvider';
import { useParams } from 'react-router-dom';
import { JokerFieldTypes, JokerTypes } from '../../config/joker';
import jokerService from '../../service/joker.service';
import { IssueMethod } from '../../config/production';
import { multiply } from 'mathjs';

const SourceListPage = ({ selectedItemCode }) => {
  const { itemCode } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { params, getParam } = useParam();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autocompleteData, setAutoCompleteData] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    quantityUnit: [],
    items: [],
    templateItems: [],
    user: [],
  });
  const [jokerFields, setJokerFields] = useState([]);
  const [jokerLineFields, setJokerLineFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const def = {
    itemCode: null,
    quantityUnit: null,
    baseQuantity: '',
    variantMainItemCode: null,
    variantNote: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
    joker_int6: '',
    joker_int7: '',
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_char11: '',
    joker_char12: '',
    joker_char13: '',
    joker_char14: '',
    joker_char15: '',
    joker_char16: '',
    joker_char17: '',
    joker_char18: '',
    joker_char19: '',
    joker_char20: '',
    joker_char21: '',
    joker_char22: '',
    joker_char23: '',
    joker_char24: '',
    joker_char25: '',
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    whsCode: null,
    binId: null,
    weight: '',
    weightPerItem: '',
    item: {},
    sourceListOperation: [],
    sourceListItem: [],
    sourceListAttachment: [],
  };

  const defualtOperationMachine = {
    id: null,
    resourceId: null,
    neededQuantity: 1,
    quantityUnitId: config?.defOperationQtyUnit,
    isNew: true,
  };

  const defualtOperation = {
    operationId: null,
    dependence: config?.defaultDependence,
    description: '',
    fixedLeadTime: '',
    sourceListOperationMachine: [],
    isNew: true,
  };

  const defaultItem = {
    itemCode: null,
    neededQuantity: 1,
    description: '',
    joker_char1: '',
    joker_num1: null,
    joker_num2: null,
    secondaryProduct: false,
    whsCode: config?.materialDefWhs ?? null,
    issueMethod: config?.productionDefIssueMethod ?? IssueMethod.MANUAL,
    weight: '',
    weightPerItem: '',
  };

  const getJokerFields = () => {
    jokerService
      .getFields(JokerTypes.SOURCELIST)
      .then((data) => {
        const [headFields, lineFields] = data.reduce(
          (acc, field) => {
            if (field.isRow) {
              acc[1].push(field);
            } else {
              acc[0].push(field);
            }
            return acc;
          },
          [[], []]
        );

        setJokerFields(headFields);
        setJokerLineFields(lineFields);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getMenus();
    getConfig();
    getParam();
    if (!Object.keys(user).length) getUser();
    initDefaultValues();
    setDefaultValues(def);
    getJokerFields();
  }, []);

  useEffect(() => {
    if (Object.keys(config).length > 0) {
      setDefaultValues({ ...def, whsCode: config?.productDefWhs ?? null, binId: config?.productDefBin ?? null });
    }
  }, [config]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('sourcelist');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    if (itemCode) {
      sourcelistService.getSourcelistById(itemCode).then((data) => {
        setSelectedValues(data);
      });
    } else {
      setSelectedValues(defaultValues);
    }
  }, [defaultValues, itemCode]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .operationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );
    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemGroupToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .templateItemsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .warehouseToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    showLoader();

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          quantityUnit: resp[0],
          operation: resp[1],
          resource: resp[2],
          itemGroup: resp[3],
          templateItems: resp[4],
          warehouse: resp[5],
          user: resp[6],
        };

        setAutoCompleteData(aData);

        const acOptions = {
          quantityUnit: generateAutocompleteItems(aData.quantityUnit, 'name', 'id'),
          items: [],
          operation: generateAutocompleteItems(aData.operation, ['code', 'operationName'], 'operationId'),
          resource: generateAutocompleteItems(aData.resource, 'name', 'id'),
          itemGroup: generateAutocompleteItems(aData.itemGroup, 'name', 'id'),
          templateItems: generateAutocompleteItems(aData.templateItems, 'name', 'id'),
          warehouse: generateAutocompleteItems(aData.warehouse, 'name', 'code'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const validationSchema = Yup.object().shape({
    itemCode: Yup.string().required(),
    baseQuantity: Yup.number().required().positive(),
    variantMainItemCode: Yup.string().nullable(),
    variantNote: Yup.string().nullable(),
    whsCode: Yup.string().nullable(),
    binId: Yup.number()
      .nullable()
      .when('whsCode', (whsCode, schema) => {
        const whs = autocompleteData?.warehouse?.find((w) => w.code === whsCode);
        return whs?.binManaged ? schema.required() : schema;
      }),
    weight: Yup.number().nullable(),
    weightPerItem: Yup.number().nullable(),
    sourceListOperation: Yup.array().of(
      Yup.object().shape({
        operationId: Yup.number().required(),
        dependence: Yup.string().nullable(),
        description: Yup.string().nullable(),
        fixedLeadTime: Yup.number().min(0).nullable(),
        sourceListOperationMachine: Yup.array()
          .of(
            Yup.object().shape({
              resourceId: Yup.number().required(),
              neededQuantity: Yup.number().positive().required(),
              quantityUnitId: Yup.number().nullable().required(),
            })
          )
          .required()
          .min(1, 'Legalább egy gép szükséges!'),
      })
    ),
    sourceListItem: Yup.array().of(
      Yup.object().shape({
        itemCode: Yup.string().required(),
        neededQuantity: Yup.number().required().positive(),
        quantityUnitId: Yup.number(),
        secondaryProduct: Yup.boolean(),
        whsCode: Yup.string().nullable(),
        issueMethod: Yup.string().nullable(),
        weight: Yup.number().nullable(),
        weightPerItem: Yup.number().nullable(),
        ...(jokerLineFields.length > 0
          ? Object.fromEntries(
              jokerLineFields.map((field) => {
                const fieldType = getJokerFieldType(field);
                return [
                  field.field,
                  fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
                ];
              })
            )
          : {}),
      })
    ),
    ...(jokerFields.length > 0
      ? Object.fromEntries(
          jokerFields.map((field) => {
            const fieldType = getJokerFieldType(field);
            return [
              field.field,
              fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
            ];
          })
        )
      : {}),
  });

  const valuesToForm = () => {
    const {
      id,
      itemCode,
      baseQuantity,
      item,
      sourceListOperation,
      sourceListItem,
      variantNote,
      variantMainItemCode,
      joker_int1,
      joker_int2,
      joker_int3,
      joker_int4,
      joker_int5,
      joker_int6,
      joker_int7,
      joker_char1,
      joker_char2,
      joker_char3,
      joker_char4,
      joker_char5,
      joker_char6,
      joker_char7,
      joker_char8,
      joker_char9,
      joker_char10,
      joker_char11,
      joker_char12,
      joker_char13,
      joker_char14,
      joker_char15,
      joker_char16,
      joker_char17,
      joker_char18,
      joker_char19,
      joker_char20,
      joker_char21,
      joker_char22,
      joker_char23,
      joker_char24,
      joker_char25,
      joker_num1,
      joker_num2,
      joker_num3,
      joker_num4,
      joker_num5,
      whsCode,
      binId,
      weight,
      weightPerItem,
      sourceListAttachment,
    } = selectedValues;

    const op = sourceListOperation?.map((so) => ({
      ...so,
    }));

    setInitValues({
      id,
      itemCode,
      baseQuantity,
      variantNote,
      variantMainItemCode,
      quantityUnitId: item?.quantityUnitId,
      joker_int1: joker_int1 ?? '',
      joker_int2: joker_int2 ?? '',
      joker_int3: joker_int3 ?? '',
      joker_int4: joker_int4 ?? '',
      joker_int5: joker_int5 ?? '',
      joker_int6: joker_int6 ?? '',
      joker_int7: joker_int7 ?? '',
      joker_char1: joker_char1 ?? '',
      joker_char2: joker_char2 ?? '',
      joker_char3: joker_char3 ?? '',
      joker_char4: joker_char4 ?? '',
      joker_char5: joker_char5 ?? '',
      joker_char6: joker_char6 ?? '',
      joker_char7: joker_char7 ?? '',
      joker_char8: joker_char8 ?? '',
      joker_char9: joker_char9 ?? '',
      joker_char10: joker_char10 ?? '',
      joker_char11: joker_char11 ?? '',
      joker_char12: joker_char12 ?? '',
      joker_char13: joker_char13 ?? '',
      joker_char14: joker_char14 ?? '',
      joker_char15: joker_char15 ?? '',
      joker_char16: joker_char16 ?? '',
      joker_char17: joker_char17 ?? '',
      joker_char18: joker_char18 ?? '',
      joker_char19: joker_char19 ?? '',
      joker_char20: joker_char20 ?? '',
      joker_char21: joker_char21 ?? '',
      joker_char22: joker_char22 ?? '',
      joker_char23: joker_char23 ?? '',
      joker_char24: joker_char24 ?? '',
      joker_char25: joker_char25 ?? '',
      joker_num1: joker_num1 ?? '',
      joker_num2: joker_num2 ?? '',
      joker_num3: joker_num3 ?? '',
      joker_num4: joker_num4 ?? '',
      joker_num5: joker_num5 ?? '',
      whsCode,
      sourceListAttachment,
      binId,
      weight,
      weightPerItem,
      item: item,
      sourceListOperation: op,
      sourceListItem: sourceListItem.map((si) => ({
        ...si,
        quantityUnitId: si.item?.quantityUnitId ?? null,
      })),
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
      sourceListOperation: values.sourceListOperation.map((so) => {
        return {
          ...cleanValues(so),
          id: so.isNew ? undefined : so.id,
          fixedLeadTime: so.fixedLeadTime ? parseFloat(so.fixedLeadTime) : 0,
          sourceListOperationMachine: so.sourceListOperationMachine.map((som) => {
            return {
              ...cleanValues(som),
              id: som.isNew ? undefined : som.id,
            };
          }),
        };
      }),
      updatedBy: user.userId,
    };

    updData.existingFiles = updData.sourceListAttachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    if (updData.sourceListAttachment) {
      for (const file of updData.sourceListAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    sourcelistService.updateSourcelist(formData, selectedValues.id).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
      createdBy: user.userId,
      sourceListOperation: values.sourceListOperation.map((so) => {
        return {
          ...cleanValues(so),
          fixedLeadTime: so.fixedLeadTime ? parseFloat(so.fixedLeadTime) : 0,
          sourceListOperationMachine: so.sourceListOperationMachine.map((som) => {
            return {
              ...cleanValues(som),
            };
          }),
        };
      }),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.sourceListAttachment) {
      for (const file of insertObject.sourceListAttachment) {
        formData.append('files', file);
      }
    }

    sourcelistService
      .createSourcelist(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  dayjs.extend(utc);

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    sourcelistService.deleteSourcelist(deleteData, selectedValues.itemCode).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultValues);
      setInitValues(defaultValues);
    });
  };

  const handleImportTemplateItem = (templateItemId, values, setFieldValue) => {
    const newVisOrder = (values.sourceListOperation?.length || 0) + 1;

    const foundedTemplateItemOperations = autocompleteData.templateItems.find(
      (item) => item.id === templateItemId
    )?.templateItemOperation;

    const importedItems = foundedTemplateItemOperations.map((item, index) => ({
      ...defualtOperation,
      id: uuidv4(),
      visOrder: newVisOrder + index,
      operationId: item.operationId,
      dependence: config?.defaultDependence,
      sourceListOperationMachine: [
        {
          ...defualtOperationMachine,
          id: uuidv4(),
          resourceId: item.operation.resourceOneId,
          neededQuantity: item.neededQty,
        },
      ],
      fixedLeadTime: 0,
    }));

    setFieldValue('sourceListOperation', [...values.sourceListOperation, ...importedItems]);
  };

  const deleteClick = () => {
    setShowDeleteDialog(true);
  };
  const displayDeleteBtn = params.SOURCELIST_NOT_DISPLAY_DELETE_BUTTON !== 'Y';

  const handleExport = () => {
    sourcelistService.getExcelData(selectedValues.itemCode).then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  return (
    <>
      <FormBase
        name="SourceList"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <div className="flex items-center flex-wrap justify-between">
                <FormHeaderUI
                  addClick={() => {
                    setInitValues(defaultValues);
                    setSelectedValues(defaultValues);
                    setViewField(false);
                    setAddField(false);
                  }}
                  addDisabled={!viewField || !canEdit}
                  modClick={() => {
                    setViewField(false);
                    setEditButtonvisible(false);
                  }}
                  modDisabled={!viewField || operatorsDisabled || !canEdit}
                  deleteClick={displayDeleteBtn && deleteClick}
                  deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                  copyClick={() => {
                    setViewField(false);
                    setAddField(false);
                  }}
                  copyDisabled={!viewField || operatorsDisabled || !canEdit}
                />
                <div className="flex items-center">
                  <History disabled={operatorsDisabled} onClick={() => setHistoryDialogOpen(!historyDialogOpen)} />
                  <IconButton disabled={operatorsDisabled} onClick={() => setShowDialog(true)}>
                    <AccountTreeIcon sx={{ fontSize: '1.8rem' }} />
                  </IconButton>
                </div>
              </div>

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <SourceListFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      autocompleteOptions={autocompleteOptions}
                      autocompleteData={autocompleteData}
                      handleImportTemplateItem={handleImportTemplateItem}
                    />
                    {!isLoading && (
                      <SourceListTabs
                        disabled={viewField}
                        defaultField={defualtOperation}
                        defualtOperationMachine={defualtOperationMachine}
                        defaultItem={defaultItem}
                        autocompleteOptions={autocompleteOptions}
                        setRefetchTable={setRefetchTable}
                        selectedValuesState={{ selectedValues, setSelectedValues }}
                        autocompleteData={autocompleteData}
                        user={user}
                        addField={addField}
                        jokerLineFields={jokerLineFields}
                        setAutoCompleteData={setAutoCompleteData}
                        setAutoCompleteOptions={setAutoCompleteOptions}
                      />
                    )}

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);
                        resetForm();
                        valuesToForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <SourceListReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autocompleteOptions={autocompleteOptions}
            selectedItemCode={selectedItemCode}
            jokerFields={jokerFields}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
      <DialogUI
        open={showDialog}
        headerContent={'Fa nézet'}
        fullWidth={true}
        onClose={() => setShowDialog(false)}
        closeBtn={true}
        maxWidth="md"
      >
        <SourceListTree itemCode={selectedValues.itemCode} handleExport={handleExport} />
      </DialogUI>
      <SourceListHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        sourceListId={selectedValues.id}
      />
    </>
  );
};

export default SourceListPage;
